import React, { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Grid,
  Paper,
  LinearProgress,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SupervisorDropdown from '../components/SupervisorDropdown';

type PerfUser = {
  id: number;
  full_name: string;
  role: 'admin' | 'supervisor' | 'student';
  tasks_assigned?: number;
  tasks_completed?: number;
  on_time_rate?: string; // e.g. '75.0'
  last_active?: string | null;
  department?: string;
  activity_level?: number[];
  total_comments?: number;
  total_projects?: number;
};

type PerfOverviewResponse = { performanceData: PerfUser[] };

function pct(n: number) {
  return Math.max(0, Math.min(100, Math.round(n)));
}

function averageCompletion(users: PerfUser[]) {
  const withTasks = users.filter(u => (u.tasks_assigned || 0) > 0);
  if (!withTasks.length) return 0;
  const sum = withTasks.reduce((s, u) => s + ((u.tasks_completed || 0) / (u.tasks_assigned || 1)) * 100, 0);
  return sum / withTasks.length;
}

function averageOnTime(users: PerfUser[]) {
  if (!users.length) return 0;
  const sum = users.reduce((s, u) => s + parseFloat(u.on_time_rate || '0'), 0);
  return sum / users.length;
}

function OverviewCards({ users, showActivity = true, activityMode = 'synthetic' }: { users: PerfUser[]; showActivity?: boolean; activityMode?: 'synthetic' | 'lastActive' }) {
  const theme = useTheme();
  const avgProgress = useMemo(() => averageCompletion(users), [users]);
  const avgOnTime = useMemo(() => averageOnTime(users), [users]);
  // Activity series
  const activitySeries = useMemo(() => {
    if (!showActivity) return [] as { label: string; value: number; names: string[] }[];
    if (activityMode === 'lastActive') {
      // Build last 7 days buckets based on last_active date
      const days: { label: string; value: number; names: string[] }[] = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setHours(0,0,0,0);
        d.setDate(d.getDate() - i);
        const label = d.toLocaleDateString();
        const names = users
          .filter(u => !!u.last_active)
          .filter(u => {
            const la = new Date(u.last_active as string);
            la.setHours(0,0,0,0);
            return la.getTime() === d.getTime();
          })
          .map(u => u.full_name);
        days.push({ label, value: names.length, names });
      }
      return days;
    }
    // Only show real activity data, no synthetic data for new accounts
    const length = Math.max(0, ...users.map(u => (u.activity_level || []).length));
    if (length > 0) {
      const arr: number[] = Array.from({ length }, (_, i) => users.reduce((s, u) => s + ((u.activity_level || [])[i] || 0), 0));
      return arr.map((v, idx) => ({ label: `W${idx + 1}`, value: v, names: [] as string[] }));
    }
    
    // For new accounts with no data, return empty array instead of synthetic data
    const total = users.reduce((s, u) => s + (u.tasks_completed || 0), 0);
    const totalComments = users.reduce((s, u) => s + (u.total_comments || 0), 0);
    const totalProjects = users.reduce((s, u) => s + (u.total_projects || 0), 0);
    
    // If no activity at all, show empty state
    if (total === 0 && totalComments === 0 && totalProjects === 0) {
      return []; // No data to show
    }
    
    // Generate activity data based on comments and projects (more realistic than tasks)
    const activityBase = Math.max(1, Math.floor((totalComments + totalProjects) / 6));
    const arr = [activityBase - 1, activityBase, activityBase + 1, activityBase + 2, activityBase + 3, activityBase + 1].map(v => Math.max(0, v));
    return arr.map((v, idx) => ({ label: `W${idx + 1}`, value: v, names: [] as string[] }));
  }, [users, showActivity, activityMode]);

  // compute locally where needed; avoid unused assignment in CI
  // const max = Math.max(1, ...activitySeries.map(a => a.value));

  return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3, height: 300, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" color="text.secondary">Task Progress</Typography>
          <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <LinearProgress variant="determinate" value={pct(avgProgress)} sx={{ height: 10, borderRadius: 5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Average</Typography>
              <Typography variant="body2" fontWeight={600}>{pct(avgProgress)}%</Typography>
                </Box>
              </Box>
        </Paper>
        </Grid>
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3, height: 300, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant="determinate" value={pct(avgOnTime)} size={84} thickness={6} />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="subtitle1" fontWeight={700}>{pct(avgOnTime)}%</Typography>
                </Box>
              </Box>
                <Box>
            <Typography variant="subtitle2" color="text.secondary">On-time Submission</Typography>
            <Typography variant="body2">Average across visible users</Typography>
                </Box>
        </Paper>
      </Grid>
      {showActivity && (
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 300, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" color="text.secondary">Activity Level</Typography>
            <Box sx={{ height: 200, mt: 1, flex: 1 }}>
              {activitySeries.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={activitySeries.map(a => ({ week: a.label, value: a.value }))} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.mode === 'dark' ? '#3b3f55' : '#e5e7eb'} />
                    <XAxis 
                      dataKey="week" 
                      tick={{ fill: theme.palette.text.primary, fontSize: 12 }} 
                      axisLine={{ stroke: theme.palette.text.secondary as any }} 
                      tickLine={false} 
                    />
                    <YAxis 
                      tick={{ fill: theme.palette.text.primary, fontSize: 12 }} 
                      axisLine={{ stroke: theme.palette.text.secondary as any }} 
                      tickLine={false} 
                      allowDecimals={false} 
                    />
                    <Tooltip
                      cursor={{ fill: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }} 
                      formatter={(value, name, props) => {
                        const week = props.payload?.week;
                        const weekIndex = activitySeries.findIndex(a => a.label === week);
                        const names = weekIndex >= 0 ? activitySeries[weekIndex].names : [];
                        const nameText = names.length > 0 ? ` (${names.join(', ')})` : '';
                        return [`${value}${nameText}`, 'Logins/Updates'];
                      }}
                      labelFormatter={(label) => `Week ${label}`}
                      labelStyle={{ color: theme.palette.mode === 'dark' ? '#fff' : '#111' }} 
                      contentStyle={{ background: theme.palette.mode === 'dark' ? '#111827' : '#ffffff', border: `1px solid ${theme.palette.mode === 'dark' ? '#374151' : '#e5e7eb'}` }} 
                    />
                    <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  flexDirection: 'column',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    No activity data available
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Activity will appear as users complete tasks
                  </Typography>
                </Box>
              )}
              </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Logins/updates per week</Typography>
          </Paper>
        </Grid>
      )}
        </Grid>
  );
}

function UsersTable({ users, showRoleFilter = true, showSupervisorFilter = false }: { users: PerfUser[]; showRoleFilter?: boolean; showSupervisorFilter?: boolean }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [supervisorFilter, setSupervisorFilter] = useState<number | ''>('');

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter(user => {
      const matchesSearch = user.full_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      const matchesSupervisor = !supervisorFilter || (user as any).supervisor_id === supervisorFilter;
      return matchesSearch && matchesRole && matchesSupervisor;
    });

    // Sort users
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.full_name.localeCompare(b.full_name);
        case 'tasks':
          return (b.tasks_completed || 0) - (a.tasks_completed || 0);
        case 'onTime':
          return parseFloat(b.on_time_rate || '0') - parseFloat(a.on_time_rate || '0');
        case 'lastActive':
          if (!a.last_active && !b.last_active) return 0;
          if (!a.last_active) return 1;
          if (!b.last_active) return -1;
          return new Date(b.last_active).getTime() - new Date(a.last_active).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [users, searchTerm, roleFilter, sortBy, supervisorFilter]);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Filter Controls */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200 }}
        />
        
        {showRoleFilter && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              label="Role"
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="all">All Roles</MenuItem>
              <MenuItem value="supervisor">Supervisor</MenuItem>
              <MenuItem value="student">Student</MenuItem>
            </Select>
          </FormControl>
        )}

        {showSupervisorFilter && (
          <SupervisorDropdown
            value={supervisorFilter}
            onChange={setSupervisorFilter}
            label="Filter by Supervisor"
            size="small"
            showAvatar={true}
            showRole={false}
            sx={{ minWidth: 200 }}
          />
        )}

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="tasks">Tasks Completed</MenuItem>
            <MenuItem value="onTime">On-time Rate</MenuItem>
            <MenuItem value="lastActive">Last Active</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredAndSortedUsers.length} of {users.length} users
                  </Typography>
                </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
            <TableCell align="right">Tasks</TableCell>
            <TableCell align="right">Completed</TableCell>
            <TableCell align="right">On-time</TableCell>
            <TableCell align="right">Last Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
          {filteredAndSortedUsers.map(u => (
            <TableRow key={u.id} hover>
              <TableCell>{u.full_name}</TableCell>
                  <TableCell>
                <Chip size="small" label={u.role} color={u.role === 'supervisor' ? 'warning' : u.role === 'student' ? 'info' : 'default'} />
                  </TableCell>
              <TableCell align="right">{u.tasks_assigned || 0}</TableCell>
              <TableCell align="right">{u.tasks_completed || 0}</TableCell>
              <TableCell align="right">{pct(parseFloat(u.on_time_rate || '0'))}%</TableCell>
              <TableCell align="right">{u.last_active ? new Date(u.last_active).toLocaleDateString() : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
      </Paper>
  );
}

function AdminDashboard({ users }: { users: PerfUser[] }) {
  const visible = useMemo(() => users.filter(u => u.role !== 'admin'), [users]);
                return (
    <Box>
      <Typography variant="h5" gutterBottom>Performance Overview (Admin)</Typography>
      <OverviewCards users={visible} showActivity={true} activityMode="lastActive" />
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Supervisors & Students</Typography>
      <UsersTable users={visible} />
                  </Box>
                );
}

function SupervisorDashboard({ users }: { users: PerfUser[] }) {
                return (
    <Box>
      <Typography variant="h5" gutterBottom>Your Students' Performance</Typography>
      <OverviewCards users={users} activityMode="lastActive" />
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Students</Typography>
      <UsersTable users={users} showRoleFilter={false} />
                  </Box>
                );
}

function StudentDashboard({ users }: { users: PerfUser[] }) {
  return (
                            <Box>
      <Typography variant="h5" gutterBottom>Your Team Performance</Typography>
      <OverviewCards users={users} />
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Team Members</Typography>
      <UsersTable users={users} showRoleFilter={false} />
                            </Box>
  );
}

const Performance: React.FC = () => {
  const { user } = useAuth();
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<number | ''>('');
  
  const { data } = useQuery<PerfOverviewResponse>(
    ['rbac-performance', selectedSupervisorId], 
    async () => {
      const params = selectedSupervisorId ? { supervisor_id: selectedSupervisorId } : {};
      const res = await api.get('/performance/overview', { params });
      return res.data;
    }, 
    { refetchOnMount: 'always', staleTime: 0 }
  );

  const allUsers = data?.performanceData || [];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Performance Dashboard</Typography>
        {user?.role === 'admin' && (
          <Box sx={{ minWidth: 200 }}>
            <SupervisorDropdown
              value={selectedSupervisorId}
              onChange={setSelectedSupervisorId}
              label="Filter by Supervisor"
              showAvatar={true}
              showRole={false}
              size="small"
            />
          </Box>
        )}
      </Box>
      {user?.role === 'admin' && <AdminDashboard users={allUsers} />}
      {user?.role === 'supervisor' && <SupervisorDashboard users={allUsers} />}
      {user?.role === 'student' && <StudentDashboard users={allUsers} />}
    </Box>
  );
};

export default Performance; 


